// src/tabs/logements/LivretInfosEditor.jsx — Phase A.7.1b (ADR-120)
// + Sprint A.9.2 PR-A92-4 PR1 (ADR-140) — adresse 5 champs activés
//   (Option A.bis Dimitri : livret_config = source unique infos opérationnelles).
// + Sprint A.9.3 PR-A93-1e (ADR-147) — TikTok + website_url admin éditeur.
//
// Sous-onglet "Infos" éditeur livret admin.
// ADR-199 : adresse / horaires / contact d'urgence édités UNIQUEMENT dans
// l'onglet Instructions (source unique).
// L2/L5 : wifi_networks RETIRÉ (doublon Instructions > WiFi, ADR-198) et
// social_networks RETIRÉ (source unique Paramètres > Pied de page livret,
// cfg.co.footer, BUG 34) — ni l'un ni l'autre dans le patch de save.
// L2c : bloc lecture seule « Adresse et horaires » RETIRÉ (doublon d'affichage
// de ce qui est édité dans Instructions). Le voyageur lit toujours ces infos
// depuis leur source.
// Cet éditeur ne garde donc QUE ce qui s'y édite vraiment :
// - website_url (URL site logement, footer voyageur bouton "Réserver") — PR-A93-1e
// + un pointeur réseaux sociaux → Paramètres.
// Lien avis Google RETIRÉ d'ici : le champ google_review_url était éditable +
// sauvegardé mais affiché NULLE PART (ni admin, ni livret voyageur, ni
// fbz-sites) = champ MORT. Source UNIQUE du lien avis = avis_google_url, édité
// dans l'onglet Instructions (LivretTab.jsx), consommé par le parcours Direct.

import { useState, useEffect } from "react";
import { DS } from "../../lib/ui";
import { Rss, Link2, Save, AlertTriangle } from "lucide-react";
import { supabase } from "../../supabase";

async function getAuthHeaders() {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  const token = session?.access_token;
  return token
    ? { "Content-Type": "application/json", Authorization: `Bearer ${token}` }
    : { "Content-Type": "application/json" };
}

// Sprint A.9.3 PR-A93-1e (ADR-147) — validation client website_url cohérente
// serveur (regex http(s):// + longueur 500 chars). Optionnel, vide ou null = OK.
function isWebsiteUrlValid(url) {
  if (!url) return true;
  return /^https?:\/\//.test(url) && url.length <= 500;
}

export default function LivretInfosEditor({ config, logement_id, onReload }) {
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  // Local-only state (debounce avant PATCH).
  // L2/L5 : wifi_networks (doublon Instructions/WifiNetworksEditor) et
  // social_networks (source unique = Paramètres > Pied de page livret,
  // cfg.co.footer) RETIRÉS de cet éditeur. Lien avis Google RETIRÉ aussi
  // (champ mort google_review_url ; source unique = avis_google_url, onglet
  // Instructions). Reste ici : website_url (spécifique livret, pas de doublon).
  const [websiteUrl, setWebsiteUrl] = useState(config?.website_url || "");

  // ADR-199 — adresse / horaires / contact d'urgence édités UNIQUEMENT dans
  // l'onglet Instructions (source unique). L2c : plus ré-affichés ici.

  // Sync local depuis prop quand config change (mount + reload externe).
  // anti-pattern F2 patterns-anti-bugs : useState(prop) sans useEffect re-sync.
  useEffect(() => {
    setWebsiteUrl(config?.website_url || "");
  }, [config]);

  async function handleSave() {
    // Sprint A.9.3 PR-A93-1e (ADR-147) — validation client website_url.
    if (!isWebsiteUrlValid(websiteUrl)) {
      setError("URL site logement invalide. Doit commencer par http:// ou https://");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const headers = await getAuthHeaders();
      // ADR-199 — hours / emergency_contact / adresse 5 champs RETIRÉS du patch :
      // ces champs sont édités dans Instructions (cfg). Les inclure ici (même
      // vides) écraserait silencieusement le fallback table par du vide.
      // L2/L5 — wifi_networks (édité dans Instructions > WiFi) et social_networks
      // (édité dans Paramètres > Pied de page livret) DÉLIBÉRÉMENT ABSENTS du
      // patch : un save Infos ne doit JAMAIS les toucher (ils ont leur source
      // unique ailleurs). Ne pas les réintroduire ici.
      const patch = {
        // website_url exposé patch admin (footer voyageur bouton "Réserver").
        website_url: websiteUrl.trim() || null,
      };
      const resp = await fetch(
        "/api/beds24-mapping?action=livret_config_upsert",
        {
          method: "PATCH",
          headers,
          body: JSON.stringify({
            action: "livret_config_upsert",
            logement_id,
            ...patch,
          }),
        },
      );
      const json = await resp.json();
      if (!resp.ok)
        throw new Error(json.message || json.error || "patch_failed");
      await onReload?.();
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div
      style={{
        background: DS.card,
        borderRadius: DS.radiusCard,
        padding: 18,
        border: `0.5px solid ${DS.border}`,
        fontFamily: DS.fontBody,
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 9,
          fontFamily: DS.fontDisplay,
          fontSize: 15,
          fontWeight: 600,
          color: DS.marineDark,
          marginBottom: 14,
        }}
      >
        <span style={{
          display: "inline-flex", width: 28, height: 28, borderRadius: 8,
          background: DS.catCielBg, color: DS.catCielIco, alignItems: "center", justifyContent: "center",
        }}>
          <Rss size={16} strokeWidth={1.8} />
        </span>
        Infos pratiques
      </div>

      {error && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 9,
            padding: 10,
            background: DS.dangerBg,
            border: `0.5px solid ${DS.danger}`,
            borderRadius: 8,
            color: DS.dangerText,
            fontSize: 12,
            marginBottom: 12,
          }}
        >
          <AlertTriangle size={15} strokeWidth={1.8} style={{ flex: "0 0 auto" }} /> {error}
        </div>
      )}

      {/* L2c — bloc lecture seule « Adresse et horaires » RETIRÉ (doublon
          d'affichage de ce qui est édité dans Instructions, même logique que le
          WiFi). Le voyageur lit toujours ces infos depuis leur source.
          L5 — WiFi RETIRÉ (source unique Instructions > WiFi, ADR-198). */}

      {/* Lien avis Google RETIRÉ de l'éditeur Infos : champ mort
          google_review_url (édité mais affiché nulle part). Source unique du
          lien avis = avis_google_url, onglet Instructions (LivretTab.jsx). */}

      {/* L2 — Réseaux sociaux RETIRÉS d'ici. Source unique = Paramètres >
          Pied de page livret (cfg.co.footer.social_networks, appliqué au footer
          de TOUS les livrets voyageur, BUG 34). */}
      <div
        style={{
          display: "flex",
          alignItems: "flex-start",
          gap: 7,
          marginBottom: 16,
          fontSize: 11,
          color: DS.textSecondary,
          fontStyle: "italic",
        }}
      >
        <Link2 size={14} strokeWidth={1.8} style={{ flex: "0 0 auto", marginTop: 1 }} />
        <span>Réseaux sociaux → édités dans <strong>Paramètres &gt; Pied de page
        livret</strong> (config unique appliquée à tous les livrets).</span>
      </div>

      {/* URL site logement — PR-A93-1e (ADR-147) — footer voyageur "Réserver ce logement" */}
      <div style={{ marginBottom: 16 }}>
        <div style={lbl}>URL site logement (optionnel)</div>
        <input
          type="url"
          value={websiteUrl}
          onChange={(e) => setWebsiteUrl(e.target.value)}
          placeholder="https://fullbnbzen.com/logement/cocon-naturel"
          maxLength={500}
          style={{
            ...inpStyle,
            borderColor:
              websiteUrl && !isWebsiteUrlValid(websiteUrl)
                ? DS.red
                : "#dde3ec",
          }}
        />
        {websiteUrl && !isWebsiteUrlValid(websiteUrl) && (
          <div style={{ fontSize: 11, color: DS.red, marginTop: 4 }}>
            URL invalide. Doit commencer par http:// ou https://
          </div>
        )}
        <div style={{ fontSize: 10, color: "#888", marginTop: 4 }}>
          Affichée en footer du livret voyageur ("Réserver ce logement").
        </div>
      </div>

      <button
        type="button"
        onClick={handleSave}
        disabled={saving}
        className="fbz-press"
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 7,
          padding: "10px 18px",
          background: saving ? "#cbd5e1" : DS.corail,
          color: "#fff",
          border: "none",
          borderRadius: DS.radiusControl,
          fontSize: 12,
          fontWeight: 600,
          cursor: saving ? "not-allowed" : "pointer",
          fontFamily: DS.fontBody,
        }}
      >
        <Save size={15} strokeWidth={1.8} /> {saving ? "Enregistrement…" : "Enregistrer toutes les infos"}
      </button>
    </div>
  );
}

// L2c — composant DerivedFromInstructions (bloc lecture seule adresse/horaires)
// RETIRÉ : doublon d'affichage de ce qui est édité dans Instructions. Le style
// derivedVal associé est retiré avec lui.

const lbl = {
  fontSize: 11,
  color: "#888",
  fontWeight: 700,
  marginBottom: 5,
  textTransform: "uppercase",
  letterSpacing: 0.4,
};

const inpStyle = {
  border: "1px solid #dde3ec",
  borderRadius: 7,
  padding: "8px 12px",
  fontSize: 13,
  outline: "none",
  background: "#f8fafd",
  fontFamily: DS.fontBody,
  width: "100%",
  boxSizing: "border-box",
};
